import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle2, KeyRound } from "lucide-react";
import Seo from "../components/Seo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    setLoading(false);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12 grid-bg">
      <Seo
        title="Reset Your Password – DriveTerrain"
        description="Send yourself a password reset link to recover access to your DriveTerrain account."
        canonical="/forgot-password"
        robots="noindex, follow"
        twitterCard="summary"
      />
      <div className="w-full max-w-md bg-card border border-border p-8" data-testid="forgot-password-card">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
          <KeyRound className="w-3.5 h-3.5 text-primary" /> Account recovery
        </div>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">Forgot password?</h1>

        {!submitted ? (
          <>
            <p className="text-sm text-muted-foreground mt-2">
              Enter the email you used to sign up. We'll send a secure link to choose a new password.
              The link expires in 30 minutes.
            </p>
            <form onSubmit={submit} className="space-y-4 mt-6">
              <div>
                <Label>Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                       required data-testid="forgot-email" className="rounded-sm" />
              </div>
              <Button type="submit" className="w-full rounded-sm" disabled={loading} data-testid="forgot-submit">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
              {err && <div className="text-sm text-destructive" data-testid="forgot-error">{err}</div>}
            </form>
            <p className="text-xs text-muted-foreground mt-5">
              Signed up with Google or mobile? Use the same method on the sign-in page —
              passwords aren't used for those accounts.
            </p>
          </>
        ) : (
          <div className="mt-6 space-y-4 text-sm" data-testid="forgot-success">
            <div className="flex items-start gap-3 border border-primary/40 bg-primary/5 p-4">
              <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-foreground">Check your inbox</div>
                <div className="text-muted-foreground mt-1">
                  If an account exists for <span className="font-medium text-foreground">{email}</span>,
                  you'll receive a password reset email shortly. Don't forget to look in spam.
                </div>
              </div>
            </div>
            <Button variant="outline" className="w-full rounded-sm" onClick={() => { setSubmitted(false); setEmail(""); }} data-testid="forgot-again">
              Use a different email
            </Button>
          </div>
        )}

        <div className="mt-6 text-sm text-muted-foreground">
          Remembered it? <Link to="/login" className="text-primary hover:underline" data-testid="back-to-login">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
